import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, Clock, Award, Bus, Users, Star, MapPin, Headset, Wallet, HeartHandshake } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import Slider from "./Slider";

const REASONS = [
  { icon: ShieldCheck, title: "100% Safe Travels", sub: "Verified drivers, GPS-ready vehicles" },
  { icon: Clock, title: "On-Time Departures", sub: "Every single trip, no exceptions" },
  { icon: Award, title: "15+ Years Trust", sub: "Serving Maharashtra since 2010" },
  { icon: Bus, title: "Modern Fleet", sub: "5 to 30-seater, AC & Non-AC" },
  { icon: Users, title: "Experienced Drivers", sub: "Licensed, courteous, well-trained" },
  { icon: Star, title: "4.9★ Rated", sub: "10,000+ happy customers" },
  { icon: MapPin, title: "Wide Coverage", sub: "All of Maharashtra & pan-India tours" },
  { icon: Headset, title: "24×7 Support", sub: "Call or WhatsApp anytime" },
  { icon: Wallet, title: "Wallet Rewards", sub: "2% credit on every completed trip" },
  { icon: HeartHandshake, title: "Family First", sub: "Kids, seniors and groups welcomed" },
];

const FALLBACK = [
  { id: "f1", text: "a promise on wheels." },
  { id: "f2", text: "your family, safe & sound." },
  { id: "f3", text: "punctual, always. period." },
  { id: "f4", text: "Maharashtra's own travel partner." },
];

function Card({ r }) {
  return (
    <div className="w-[70vw] max-w-[280px] p-5 rounded-2xl bg-white/95 border border-white/40 backdrop-blur card-lift" data-testid={`why-choose-${r.title.replace(/\s/g,'-').toLowerCase()}`}>
      <div className="w-12 h-12 rounded-xl bg-[var(--brand-orange)]/10 text-[var(--brand-orange)] grid place-items-center mb-3">
        <r.icon className="w-6 h-6" />
      </div>
      <div className="font-display font-bold text-[var(--brand-navy)]">{r.title}</div>
      <div className="text-sm text-slate-600 mt-1">{r.sub}</div>
    </div>
  );
}

export default function WhyChooseUs() {
  const t = useT();
  const [slogans, setSlogans] = useState(FALLBACK);
  const [sloganIdx, setSloganIdx] = useState(0);
  const trackedRef = useRef(new Set());

  useEffect(() => {
    api.get("/slogans").then(r => {
      if (Array.isArray(r.data) && r.data.length > 0) setSlogans(r.data);
    }).catch(()=>{});
  }, []);

  useEffect(() => {
    if (slogans.length <= 1) return;
    const t = setInterval(() => setSloganIdx(i => (i + 1) % slogans.length), 3200);
    return () => clearInterval(t);
  }, [slogans]);

  useEffect(() => {
    const s = slogans[sloganIdx];
    if (!s || !s.id || String(s.id).startsWith("f")) return;
    if (trackedRef.current.has(s.id)) return;
    trackedRef.current.add(s.id);
    api.post(`/slogans/${s.id}/impression`).catch(()=>{});
  }, [sloganIdx, slogans]);

  const handleCtaClick = () => {
    const s = slogans[sloganIdx];
    if (s?.id && !String(s.id).startsWith("f")) {
      api.post(`/slogans/${s.id}/click`).catch(()=>{});
    }
    window.location.href = "/book";
  };

  return (
    <section className="relative mt-16 mb-8" data-testid="why-choose-us">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0f1c56] via-[#14205a] to-[#0b1440]" />
      <div className="relative py-14 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 text-center text-white">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-bold text-amber-300">
            {t("why.eyebrow")}
          </div>
          <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-display font-black tracking-tight">
            {t("why.title_prefix")}
          </h2>
          <div className="relative h-14 sm:h-16 md:h-20 mt-2 overflow-hidden">
            {slogans.map((s, i) => (
              <span
                key={s.id || i}
                data-testid={`slogan-${i}`}
                className={`absolute inset-0 flex items-center justify-center px-4 text-2xl sm:text-3xl md:text-5xl font-display font-black tracking-tight text-[var(--brand-orange)] transition-all duration-700 ease-out ${
                  i === sloganIdx ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
                }`}
              >
                {s.text}
              </span>
            ))}
          </div>
          <p className="mt-3 text-white/75 max-w-2xl mx-auto text-sm sm:text-base">
            {t("why.subtitle")}
          </p>
        </div>

        <div className="mt-10 max-w-7xl mx-auto px-8 md:px-10">
          <Slider testid="why-choose-slider" autoPlay interval={4200}>
            {REASONS.map((r, i) => <Card key={i} r={r} />)}
          </Slider>
        </div>

        <div className="mt-10 text-center">
          <button onClick={handleCtaClick} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--brand-orange)] hover:bg-[var(--brand-orange-2)] text-white font-bold text-sm shadow-2xl shadow-orange-500/30 hover:-translate-y-0.5" data-testid="why-choose-cta">
            {t("why.cta")}
          </button>
        </div>
      </div>
    </section>
  );
}
